import {DomainNameEnum} from '../../../provider/domain-config/domain-config-type';
import {getPreviewSrc} from '../../../service/phototool/phototool-helper';

export type ReviewVideoEmbedType = 'direct' | 'iframe';

const DIRECT_VIDEO_PATH_REGEXP = /\.(mp4|webm|mov|m4v|ogg)(\?|#|$)/i;
const PLAYER_PATH_SEGMENT = '/player/';
const REVIEW_VIDEO_PREVIEW_PROXY_WIDTH = 256;

function parseMediaUrl(url: string): URL | null {
    try {
        return new URL(url);
    } catch {
        return null;
    }
}

function isDirectVideoFileUrl(url: URL): boolean {
    if (url.protocol === 'blob:') {
        return true;
    }

    return DIRECT_VIDEO_PATH_REGEXP.test(url.pathname);
}

function isPlayerPageUrl(url: URL): boolean {
    return url.pathname.includes(PLAYER_PATH_SEGMENT);
}

function isReviewVideoIframeEmbedUrl(url: string): boolean {
    const parsedUrl = parseMediaUrl(url);

    if (!parsedUrl || isDirectVideoFileUrl(parsedUrl)) {
        return false;
    }

    return isPlayerPageUrl(parsedUrl);
}

export function getReviewVideoEmbedType(src: string): ReviewVideoEmbedType {
    return isReviewVideoIframeEmbedUrl(src) ? 'iframe' : 'direct';
}

export function getEmbedVideoMetadataJsonUrl(videoUrl: string): string | null {
    const parsedUrl = parseMediaUrl(videoUrl);

    if (!parsedUrl || !isPlayerPageUrl(parsedUrl)) {
        return null;
    }

    parsedUrl.searchParams.set('format', 'json');

    return parsedUrl.toString();
}

export function isUsableVideoPosterUrl(poster: string | null, videoSrc: string): poster is string {
    if (!poster || poster.trim() === '') {
        return false;
    }

    if (poster === videoSrc) {
        return false;
    }

    return !isReviewVideoIframeEmbedUrl(poster);
}

function shouldProxyReviewVideoPreviewImage(src: string): boolean {
    if (src.startsWith('data:') || src.startsWith('blob:')) {
        return false;
    }

    const parsedUrl = parseMediaUrl(src);

    if (!parsedUrl || parsedUrl.protocol !== 'https:') {
        return false;
    }

    return !isReviewVideoIframeEmbedUrl(src);
}

export function getReviewVideoPreviewDisplaySrc(options: {domainName: DomainNameEnum | string; src: string}): string {
    const {domainName, src} = options;

    if (!shouldProxyReviewVideoPreviewImage(src)) {
        return src;
    }

    return getPreviewSrc({
        domainName,
        imageProxyParameters: {width: REVIEW_VIDEO_PREVIEW_PROXY_WIDTH},
        sourceSrc: src,
    });
}
